<script setup>
  /* global defineProps */
  import { computed } from 'vue'
  import { formatReplayLanguage } from './replayLabels'

  const props = defineProps({ task: { type: Object, default: null } })

  const eligibilityLabels = {
    UNKNOWN: '待校验',
    WAITING: '数据最终化中',
    ELIGIBLE: '数据已就绪',
    INELIGIBLE: '数据不合格',
  }
  const requestLabels = {
    UNRESOLVED: '策略待解析',
    NOT_REQUESTED: '等待生成请求',
    REQUESTED: '已提交生成请求',
  }
  const sourceLabels = { AUTO: '自动', MANUAL: '手动', LEGACY: '历史' }
  const statusLabels = {
    PENDING: '等待处理',
    PROCESSING: '生产中',
    COMPLETED: '全部完成',
    PARTIAL_FAILED: '部分失败',
    FAILED: '生产失败',
    CANCELLED: '已取消',
    SKIPPED: '已跳过',
  }

  const phaseCaption = computed(() => {
    const phase = props.task?.phase
    if (!phase) return '等待生产阶段'
    if (phase.startsWith('TTS_')) return `${formatReplayLanguage(phase.slice(4))} 语种成片生产中`
    if (phase.startsWith('OUTPUT_RETRY_PENDING:')) return '单语种重试排队中'
    return (
      {
        WAITING_FOR_DATA: '会议数据最终化中',
        WAITING_DATA: '会议数据最终化中',
        POLICY_PENDING: '等待生成策略解析',
        AWAITING_MANUAL_REQUEST: '数据已就绪，等待手动生成',
        READY_TO_EXECUTE: '等待生产节点认领',
        QUEUED: '任务已入队',
        RETRY_PENDING: '任务重试排队中',
        RECOVERY_RETRY: '恢复任务排队中',
        ORIGINAL_VIDEO: '原声成片生产中',
        QUALITY_CHECK: '媒体质量门禁校验中',
        COMPLETED: '产物已通过质量门禁',
        PARTIAL_FAILED: '部分语种未通过',
        FAILED: '生产阶段失败',
        QUEUE_REJECTED: '生产队列拒绝任务',
      }[phase] || phase
    )
  })

  const steps = computed(() => {
    const task = props.task
    const source = sourceLabels[task?.generationRequestSource]
    return [
      {
        key: 'candidate',
        title: '候选登记',
        caption: task ? `回放合同 v${task.replayContractVersion || '—'}` : '尚未发现合法 v2 采集',
      },
      {
        key: 'data',
        title: '数据最终化',
        caption: eligibilityLabels[task?.eligibilityStatus] || '等待资格状态',
      },
      {
        key: 'request',
        title: '生成请求',
        caption: `${requestLabels[task?.generationRequestState] || '等待请求状态'}${source ? ` · ${source}` : ''}`,
      },
      { key: 'production', title: '媒体生产', caption: phaseCaption.value },
      { key: 'quality', title: '质量门禁', caption: isQualityPhase.value ? '正在校验音视频产物' : '等待媒体质量校验' },
      { key: 'result', title: '完成 / 跳过', caption: statusLabels[task?.status] || '等待最终结果' },
    ]
  })

  const isQualityPhase = computed(() => {
    const phase = props.task?.phase || ''
    return phase === 'QUALITY_CHECK' || phase.startsWith('QUALITY_')
  })

  const isSkipped = computed(
    () =>
      props.task?.eligibilityStatus === 'INELIGIBLE' ||
      ['SKIPPED', 'CANCELLED'].includes(props.task?.status) ||
      props.task?.phase === 'INELIGIBLE'
  )

  const stepState = (key) => {
    const task = props.task
    if (!task) return 'upcoming'

    if (key === 'candidate') return 'complete'
    if (key === 'data') {
      if (task.eligibilityStatus === 'INELIGIBLE') return 'failed'
      if (task.eligibilityStatus === 'ELIGIBLE') return 'complete'
      return 'active'
    }
    if (key === 'request') {
      if (isSkipped.value || ['UNKNOWN', 'WAITING'].includes(task.eligibilityStatus)) return 'upcoming'
      if (task.generationRequestState === 'REQUESTED') return 'complete'
      if (['UNRESOLVED', 'NOT_REQUESTED'].includes(task.generationRequestState)) return 'active'
      return 'upcoming'
    }
    if (key === 'production') {
      if (isSkipped.value) return 'upcoming'
      if (task.status === 'COMPLETED' || isQualityPhase.value) return 'complete'
      if (task.status === 'PARTIAL_FAILED') return 'partial'
      if (task.status === 'FAILED') return 'failed'
      if (
        task.status === 'PROCESSING' ||
        (task.generationRequestState === 'REQUESTED' && task.eligibilityStatus === 'ELIGIBLE')
      ) {
        return 'active'
      }
      return 'upcoming'
    }
    if (key === 'quality') {
      if (task.status === 'COMPLETED') return 'complete'
      if (task.status === 'PARTIAL_FAILED') return 'partial'
      if (task.status === 'FAILED' && isQualityPhase.value) return 'failed'
      if (isQualityPhase.value) return 'active'
      return 'upcoming'
    }
    if (task.status === 'COMPLETED') return 'complete'
    if (task.status === 'PARTIAL_FAILED') return 'partial'
    if (task.status === 'FAILED') return 'failed'
    if (isSkipped.value) return 'skipped'
    return 'upcoming'
  }
</script>

<template>
  <section class="pipeline-card">
    <div class="pipeline-heading">
      <div>
        <span>PRODUCTION PIPELINE</span>
        <h2>生产流水线</h2>
      </div>
      <p>由生成请求、数据资格与后端阶段联合决定</p>
    </div>
    <div class="pipeline-track">
      <div v-for="(step, index) in steps" :key="step.key" :class="['pipeline-step', stepState(step.key)]">
        <div class="step-index">{{ String(index + 1).padStart(2, '0') }}</div>
        <div>
          <strong>{{ step.title }}</strong>
          <span>{{ step.caption }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
  .pipeline-card {
    padding: 22px 24px 26px;
    margin-top: 16px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  }

  .pipeline-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;

    span {
      color: var(--el-color-primary, #1890ff);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.17em;
    }

    h2 {
      margin: 5px 0 0;
      color: #303133;
      font-size: 20px;
      font-weight: 500;
    }

    p {
      margin: 0;
      color: #909399;
      font-size: 12px;
    }
  }

  .pipeline-track {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    margin-top: 28px;
  }

  .pipeline-step {
    position: relative;
    display: flex;
    min-width: 0;
    padding-right: 18px;
    align-items: center;
    gap: 10px;

    &::after {
      position: absolute;
      top: 17px;
      right: 6px;
      left: 40px;
      height: 1px;
      background: #e4e7ed;
      content: '';
    }

    &:last-child::after {
      display: none;
    }

    > div:last-child {
      display: flex;
      z-index: 1;
      min-width: 0;
      flex-direction: column;
      gap: 3px;
    }

    strong {
      color: #909399;
      font-size: 12px;
      white-space: nowrap;
    }

    span {
      overflow: hidden;
      color: #c0c4cc;
      font-size: 10px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &.complete,
    &.active {
      .step-index {
        color: #fff;
        background: var(--el-color-primary, #1890ff);
        border-color: var(--el-color-primary, #1890ff);
      }

      strong {
        color: #303133;
      }
    }

    &.complete::after {
      background: #8cc8ff;
    }

    &.active .step-index {
      box-shadow: 0 0 0 5px rgba(24, 144, 255, 0.14);
    }

    &.failed,
    &.partial {
      .step-index {
        color: #fff;
        background: #f56c6c;
        border-color: #f56c6c;
      }

      strong {
        color: #c45656;
      }
    }

    &.partial .step-index {
      background: #e6a23c;
      border-color: #e6a23c;
    }

    &.skipped {
      .step-index {
        color: #fff;
        background: #909399;
        border-color: #909399;
      }

      strong {
        color: #606266;
      }
    }
  }

  .step-index {
    display: grid;
    z-index: 2;
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    place-items: center;
    color: #909399;
    background: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 50%;
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 10px;
  }

  @media (max-width: 1100px) {
    .pipeline-track {
      grid-template-columns: repeat(3, 1fr);
      row-gap: 24px;
    }

    .pipeline-step:nth-child(3)::after {
      display: none;
    }
  }

  @media (max-width: 680px) {
    .pipeline-heading {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    .pipeline-track {
      grid-template-columns: 1fr 1fr;
    }

    .pipeline-step:nth-child(2n)::after {
      display: none;
    }
  }
</style>
